import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { PageKeyMap, PageLoadedSelectorsMap } from '../types/setup';
import { LoadEvent, FetchEvent } from '../types/event';
import { isOtelLogging } from '../constants/setup';

interface EventStore {
  userId: string;
  pagePath: string;
  pageKey: string;
  pageKeyMap: PageKeyMap;
  pageLoadedSelectorsMap: PageLoadedSelectorsMap;
  loadEvents: LoadEvent[];
  fetchEvents: FetchEvent[];
  isLoaded: boolean;
  initStore: (pageKey: string, pagePath: string, pageKeyMap: PageKeyMap, pageLoadedSelectorsMap: PageLoadedSelectorsMap, userId: string) => void;
  resetStore: (pageKey: string, pagePath: string) => void;
  addLoadEvent: (newLoadEvent: LoadEvent) => void;
  addFetchEvent: (newFetchEvent: FetchEvent) => void;
  setIsLoaded: (isLoaded: boolean) => void;
}

const useEventStore = create<EventStore>()(
  devtools(
    immer((set) => ({
      // state
      userId: '',
      pageKey: 'unknown',
      pagePath: 'unknown',
      pageKeyMap: { homepage: '' },
      pageLoadedSelectorsMap: { homepage: [] },
      loadEvents: [],
      fetchEvents: [],
      isLoaded: false,
      // lifecycle
      initStore: (pageKey: string, pagePath: string, pageKeyMap: PageKeyMap, pageLoadedSelectorsMap: PageLoadedSelectorsMap, userId: string) =>
        set(
          () => ({
            userId,
            pageKey,
            pagePath,
            pageKeyMap,
            pageLoadedSelectorsMap,
          }),
          undefined,
          'initStore'
        ),
      resetStore: (pageKey: string, pagePath: string) =>
        set(
          (state) => ({
            pagePath,
            pageKey,
            loadEvents: state.pagePath === pagePath ? state.loadEvents : [],
            fetchEvents: [],
            isLoaded: false,
          }),
          undefined,
          'resetStore'
        ),
      // events
      addLoadEvent: (newLoadEvent: LoadEvent) =>
        set(
          (state) => ({
            loadEvents: [...state.loadEvents, newLoadEvent]
          }),
          undefined,
          'addLoadEvent'
        ),
      addFetchEvent: (newFetchEvent: FetchEvent) =>
        set(
          (state) => ({
            fetchEvents: [...state.fetchEvents, newFetchEvent]
          }),
          undefined,
          'addFetchEvent'
        ),
      // status
      setIsLoaded: (isLoaded: boolean) =>
        set(
          () => ({
            isLoaded
          }),
          undefined,
          'setIsLoaded'
        ),
    })),
    {
      name: 'useEventStore',
      store: 'EventStore',
      enabled: isOtelLogging,
    }
  )
);

export { useEventStore };
